import { ref } from 'vue'
import axios from 'axios'
import { sendGraphQLQuery } from '@/services/gql-util'
import { Feed, FeedItem } from '@/types/Feeds'

export function useFeeds() {
	const feeds = ref<Feed[]>([])
	const feedItems = ref<FeedItem[]>([])
	const loading = ref(false)
	const error = ref<string | null>(null)

	const getFeeds = async (vhost: string): Promise<void> => {
		loading.value = true
		error.value = null

		const query = `
		query ($vhost: Int!) {
			Feeds {
				feedList(vhost: $vhost) {
					id
					name
					vhost
					url
					type
					status
					lastFetchDate
					itemCount
					autoImport
					importChannel
					importGroup
					schedule
					createdDate
				}
			}
		}
		`

		try {
			const data = await sendGraphQLQuery(query, { vhost: +vhost })
			if (data?.Feeds?.feedList) {
				feeds.value = [...data.Feeds.feedList]
			}
		} catch (err: any) {
			console.error('Error fetching feeds:', err)
			error.value = err.message || 'Failed to fetch feeds'
			// Fallback to REST
			try {
				const response = await axios.post('/services/json', {
					method: 'feeds.getFeeds',
					vhostID: vhost,
				})
				if (response.data.status !== false) {
					feeds.value = response.data.result?.data || []
				}
			} catch (restErr) {
				console.error('REST fallback also failed:', restErr)
			}
		} finally {
			loading.value = false
		}
	}

	const getFeedItems = async (feedId: number, start: number = 0, limit: number = 25): Promise<void> => {
		loading.value = true

		const query = `
		query ($feedId: Int!, $start: Int, $limit: Int) {
			Feeds {
				feedItems(feedId: $feedId, start: $start, limit: $limit) {
					id
					feedId
					title
					description
					url
					thumbUrl
					publishDate
					imported
					mediaId
				}
			}
		}
		`

		try {
			const data = await sendGraphQLQuery(query, { feedId, start, limit })
			if (data?.Feeds?.feedItems) {
				feedItems.value = [...data.Feeds.feedItems]
			}
		} catch (err: any) {
			console.error('Error fetching feed items:', err)
		} finally {
			loading.value = false
		}
	}

	const createFeed = async (vhost: string, feed: Partial<Feed>): Promise<number | null> => {
		const mutation = `
		mutation ($vhost: Int!, $name: String!, $url: String!, $type: String, $autoImport: Boolean) {
			FeedMutations {
				createFeed(vhost: $vhost, name: $name, url: $url, type: $type, autoImport: $autoImport)
			}
		}
		`

		try {
			const data = await sendGraphQLQuery(mutation, {
				vhost: +vhost,
				name: feed.name,
				url: feed.url,
				type: feed.type || 'rss',
				autoImport: feed.autoImport || false,
			})
			return data?.FeedMutations?.createFeed || null
		} catch (err: any) {
			console.error('Error creating feed:', err)
			// Fallback to REST
			try {
				const response = await axios.post('/services/json', {
					method: 'feeds.createFeed',
					vhostID: vhost,
					...feed,
				})
				return response.data.result || null
			} catch (restErr) {
				return null
			}
		}
	}

	const deleteFeed = async (feedId: number): Promise<boolean> => {
		const mutation = `
		mutation ($feedId: Int!) {
			FeedMutations {
				deleteFeed(feedId: $feedId)
			}
		}
		`

		try {
			await sendGraphQLQuery(mutation, { feedId })
			return true
		} catch (err) {
			console.error('Error deleting feed:', err)
			return false
		}
	}

	const refreshFeed = async (feedId: number): Promise<boolean> => {
		try {
			const response = await axios.post('/services/json', {
				method: 'feeds.refreshFeed',
				feedID: feedId,
			})
			return response.data.status !== false
		} catch (err) {
			console.error('Error refreshing feed:', err)
			return false
		}
	}

	const importFeedItem = async (feedItemId: number): Promise<boolean> => {
		const mutation = `
		mutation ($feedItemId: Int!) {
			FeedMutations {
				importFeedItem(feedItemId: $feedItemId)
			}
		}
		`

		try {
			await sendGraphQLQuery(mutation, { feedItemId })
			return true
		} catch (err) {
			console.error('Error importing feed item:', err)
			return false
		}
	}

	return {
		feeds,
		feedItems,
		loading,
		error,
		getFeeds,
		getFeedItems,
		createFeed,
		deleteFeed,
		refreshFeed,
		importFeedItem,
	}
}
